import React from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import type {
  AdminHealthDto,
  AdminHealthStatus,
  AdminLicenseDto,
  AdminTicketDto,
  AdminTicketStatus,
  DashboardStatsDto,
} from '../types';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { superAdminTooltips } from '../../../shared/config/actionHelpCatalog';
import { formatAdminUtcDate, getAdminUtcDaysUntil } from '../lib/adminDates';
import { AdminButton, StatusPill } from './components';
import './styles/adminDashboard.css';

type DashboardPillTone =
  | 'success'
  | 'warning'
  | 'critical'
  | 'accent';

export interface DashboardStatsProps {
  stats: DashboardStatsDto;
  health?: AdminHealthDto;
  expiringLicenses?: AdminLicenseDto[];
  latestTickets?: AdminTicketDto[];
  now?: Date;
  stale?: boolean;
  loading?: boolean;
  error?: string | null;
  retry?: () => void;
  disambiguateMetricLinks?: boolean;
}

interface AdminLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
}

interface MetricCardProps {
  accessibleName: string;
  help?: React.ComponentProps<typeof HelpTooltipTrigger>['help'];
  helpLabel?: string;
  label: string;
  note: React.ReactNode;
  tone?: 'success' | 'warning';
  to: string;
  value: React.ReactNode;
}

const AdminLink: React.FC<AdminLinkProps> = ({ children, to, ...props }) => {
  const inRouter = useInRouterContext();
  return inRouter
    ? <Link to={to} {...props}>{children}</Link>
    : <a href={to} {...props}>{children}</a>;
};

const formatRelativeTime = (value: string, now: Date) => {
  const minutes = Math.max(1, Math.round((now.getTime() - Date.parse(value)) / 60_000));
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  return days < 30 ? `${days}d ago` : formatAdminUtcDate(value);
};

const healthTone = (status: AdminHealthStatus): DashboardPillTone => {
  if (status === 'online') {
    return 'success';
  }
  return status === 'degraded' ? 'warning' : 'critical';
};

const ticketTone = (status: AdminTicketStatus): DashboardPillTone => {
  if (status === 'new') {
    return 'accent';
  }
  return status === 'open' ? 'warning' : 'success';
};

const ticketLabel = (status: AdminTicketStatus) =>
  `${status.charAt(0).toUpperCase()}${status.slice(1)}`;

const MetricCard: React.FC<MetricCardProps> = ({
  accessibleName,
  help,
  helpLabel,
  label,
  note,
  tone,
  to,
  value,
}) => (
  <article className={`admin-dashboard-stat${tone ? ` admin-dashboard-stat--${tone}` : ''}`}>
    <AdminLink
      aria-label={accessibleName}
      className="admin-dashboard-stat__link"
      to={to}
    >
      <span className="admin-dashboard-stat__label">{label}</span>
      <span className="admin-dashboard-stat__value">{value}</span>
      <span className="admin-dashboard-stat__note">{note}</span>
    </AdminLink>
    {help && helpLabel && (
      <span className="admin-dashboard-stat__help">
        <HelpTooltipTrigger label={helpLabel} help={help} placement="bottom-start" />
      </span>
    )}
  </article>
);

const ExpiringPanel: React.FC<{
  licenses: AdminLicenseDto[];
  now: Date;
}> = ({ licenses, now }) => (
  <section aria-labelledby="admin-expiring-title" className="admin-dashboard-panel">
    <header className="admin-dashboard-panel__header">
      <h2 id="admin-expiring-title">Expiring within 60 days</h2>
      <AdminLink className="admin-dashboard-panel__link" to="/super-admin/licenses">
        All licenses &rarr;
      </AdminLink>
    </header>
    {licenses.length === 0 ? (
      <p className="admin-dashboard-panel__empty">Nothing expiring soon.</p>
    ) : (
      <ul className="admin-dashboard-panel__list">
        {licenses.map((license) => {
          const days = license.dueTime
            ? getAdminUtcDaysUntil(license.dueTime, now)
            : 0;
          return (
            <li className="admin-dashboard-panel__row" key={license.id}>
              <span className="admin-dashboard-panel__key">{license.activationKey}</span>
              <span className="admin-dashboard-panel__meta">{license.companyName}</span>
              <StatusPill tone={days <= 14 ? 'critical' : 'warning'}>{days}d</StatusPill>
              <time
                className="admin-dashboard-panel__date"
                dateTime={license.dueTime ?? undefined}
              >
                {formatAdminUtcDate(license.dueTime)}
              </time>
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

const LatestTicketsPanel: React.FC<{
  now: Date;
  tickets: AdminTicketDto[];
}> = ({ now, tickets }) => (
  <section aria-labelledby="admin-latest-title" className="admin-dashboard-panel">
    <header className="admin-dashboard-panel__header">
      <h2 id="admin-latest-title">Latest requests</h2>
      <AdminLink className="admin-dashboard-panel__link" to="/super-admin/requests">
        Open inbox &rarr;
      </AdminLink>
    </header>
    {tickets.length === 0 ? (
      <p className="admin-dashboard-panel__empty">No requests yet.</p>
    ) : (
      <ul className="admin-dashboard-panel__list">
        {tickets.map((ticket) => (
          <li key={ticket.id}>
            <AdminLink
              className="admin-dashboard-panel__row admin-dashboard-panel__row--link"
              to={`/super-admin/requests?ticket=${ticket.id}`}
            >
              <span className="admin-dashboard-panel__subject">{ticket.subject}</span>
              <time className="admin-dashboard-panel__meta" dateTime={ticket.updatedAt}>
                {formatRelativeTime(ticket.updatedAt, now)}
              </time>
              <StatusPill tone={ticketTone(ticket.status)}>
                {ticketLabel(ticket.status)}
              </StatusPill>
            </AdminLink>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const DashboardMetricGrid: React.FC<{
  disambiguateLinks: boolean;
  stats: DashboardStatsDto;
}> = ({ disambiguateLinks, stats }) => (
  <div className="admin-dashboard__stats">
    <MetricCard
      accessibleName={
        disambiguateLinks
          ? `Dashboard metric: ${stats.activeLicenses} active licenses`
          : `Active licenses ${stats.activeLicenses} of ${stats.totalLicenses} total`
      }
      help={superAdminTooltips.totalLicenses}
      helpLabel="Total licenses"
      label="Active licenses"
      note={<>of {stats.totalLicenses} total</>}
      to="/super-admin/licenses"
      value={stats.activeLicenses}
    />
    <MetricCard
      accessibleName={
        disambiguateLinks
          ? `Dashboard metric: ${stats.activeInstances} of ${stats.seatsMax} seats in use`
          : `Seats in use ${stats.activeInstances} / ${stats.seatsMax}`
      }
      help={superAdminTooltips.activeInstances}
      helpLabel="Active instances"
      label="Seats in use"
      note="across all active licenses"
      to="/super-admin/licenses"
      value={
        <>
          {stats.activeInstances}
          <span className="admin-dashboard-stat__secondary"> / {stats.seatsMax}</span>
        </>
      }
    />
    <MetricCard
      accessibleName={
        disambiguateLinks
          ? `Dashboard metric: ${stats.totalUsers} users`
          : `Users ${stats.totalUsers} ${stats.blockedUsers} blocked \u00b7 ${stats.invitedUsers} invited`
      }
      help={superAdminTooltips.totalUsers}
      helpLabel="Total users"
      label="Users"
      note={<>{stats.blockedUsers} blocked {'\u00b7'} {stats.invitedUsers} invited</>}
      to="/super-admin/users"
      value={stats.totalUsers}
    />
    <MetricCard
      accessibleName={
        disambiguateLinks
          ? `Dashboard metric: ${stats.unresolvedTickets} open requests`
          : `Open requests ${stats.unresolvedTickets} ${stats.newTickets} awaiting first reply`
      }
      label="Open requests"
      note={<>{stats.newTickets} awaiting first reply</>}
      tone={stats.unresolvedTickets ? 'warning' : 'success'}
      to="/super-admin/requests"
      value={stats.unresolvedTickets}
    />
  </div>
);

const DashboardStats: React.FC<DashboardStatsProps> = ({
  stats,
  health = { licenseServer: 'offline' },
  expiringLicenses = [],
  latestTickets = [],
  now = new Date(),
  stale = false,
  loading = false,
  error = null,
  retry,
  disambiguateMetricLinks = false,
}) => {
  return (
    <section
      aria-busy={loading}
      aria-label="Admin dashboard"
      className="admin-dashboard"
      data-ready={String(!loading)}
      data-testid="admin-dashboard"
    >
      <header className="admin-dashboard__header">
        <h1>Overview</h1>
        <StatusPill
          className={`admin-dashboard__health admin-dashboard__health--${health.licenseServer}`}
          tone={healthTone(health.licenseServer)}
        >
          License server {'\u00b7'} {health.licenseServer}
        </StatusPill>
      </header>

      {stale && (
        <div className="admin-dashboard__stale" role="status">
          Refresh failed; showing the last good data.
        </div>
      )}
      {error && !stale && (
        <div className="admin-dashboard__error" role="alert">
          <span>{error}</span>
          {retry && (
            <AdminButton onClick={retry}>
              Retry
            </AdminButton>
          )}
        </div>
      )}

      <DashboardMetricGrid
        disambiguateLinks={disambiguateMetricLinks}
        stats={stats}
      />

      <div className="admin-dashboard__panels">
        <ExpiringPanel licenses={expiringLicenses} now={now} />
        <LatestTicketsPanel now={now} tickets={latestTickets} />
      </div>
    </section>
  );
};

export default DashboardStats;
